/**
 * @author        calvin@zumper.com (Calvin)
 * @copyright     Copyright (c) 2019, Zumper
 * @description   ModalWrapper component. Handles preloading and rendering of modal loadable components
 */

import type { Location } from 'history'
import React, { useEffect, useState } from 'react'
import { logger } from '../../logger'

export interface ModalWrapperProps {
  component: React.ComponentType
  location: Location<unknown>
  isMobile: boolean
  onClose: () => void
  onOpen: () => void
  open: boolean
}

export const ModalWrapper = ({
  component,
  isMobile,
  location,
  onClose,
  onOpen,
  open,
}: ModalWrapperProps) => {
  const [scrollY, setScrollY] = useState(-1) // -1 means unset

  useEffect(() => {
    if (open) {
      onOpen()
    } else {
      onClose()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  useEffect(() => {
    if (isMobile) {
      const hasModalHash = location?.hash?.includes?.('#modal=')
      if (open && hasModalHash && scrollY === -1 && window.scrollY !== 0) {
        setScrollY(window.scrollY)
      }
      if (!open && !hasModalHash && scrollY !== -1) {
        window.scrollTo(0, scrollY)
        setScrollY(-1)
      }
    }
  }, [isMobile, location.hash, open, scrollY])

  if (!component) {
    logger.error(new Error('Modal wrapper missing Loadable component'))
    return null
  }
  return open ? React.createElement(component) : null
}
